import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CreditCard, Truck, CheckCircle, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, fetchCart } = useCart();
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  const subscriptionPlan = location.state?.subscription || null;
  const isSubscriptionCheckout = !!subscriptionPlan;
  const [shippingAddress, setShippingAddress] = useState({
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India"
  });

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (!shippingAddress.full_name || !shippingAddress.phone || !shippingAddress.address_line1 || 
        !shippingAddress.city || !shippingAddress.state || !shippingAddress.pincode) {
      toast.error("Please fill all required fields");
      return;
    }
    setStep(2);
  };

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);
    
    try {
      const token = getToken();
      
      // Create order
      const orderResponse = await axios.post(
        `${API}/orders`,
        {
          items: cart.items.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            subscription: item.subscription || false,
            subscription_frequency: item.subscription_frequency
          })),
          shipping_address: shippingAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, razorpay_order_id, razorpay_key_id, total } = orderResponse.data;

      if (!razorpay_order_id || !razorpay_key_id) {
        // Mock payment for demo
        toast.success("Order placed successfully! (Demo Mode)");
        await fetchCart();
        navigate("/profile");
        return;
      }

      // Load Razorpay
      const loaded = await loadRazorpay();
      if (!loaded) {
        toast.error("Failed to load payment gateway");
        setLoading(false);
        return;
      }

      const options = {
        key: razorpay_key_id,
        amount: total * 100,
        currency: "INR",
        name: "Praroha Greens",
        description: "Fresh Microgreens Order",
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(
              `${API}/orders/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success("Payment successful! Your order has been confirmed.", { duration: 5000 });
            await fetchCart();
            navigate("/profile");
          } catch (error) {
            toast.error("Payment verification failed. Please contact support if amount was deducted.", { duration: 6000 });
          }
        },
        prefill: {
          name: shippingAddress.full_name,
          contact: shippingAddress.phone
        },
        theme: {
          color: "#1A4D2E"
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled. Your order is saved — you can try again anytime.", { duration: 4000 });
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        toast.error("Payment unsuccessful. Please try again or use a different payment method.", { duration: 5000 });
      });
      rzp.open();
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to process order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20 lg:pt-24 min-h-screen bg-background" data-testid="checkout-page">
      <SEO title="Checkout" path="/checkout" />
      {/* Header */}
      <section className="py-8 bg-praroha-cream/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-praroha-forest">
            Checkout
          </h1>
          
          {/* Steps */}
          <div className="flex items-center gap-4 mt-6">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-praroha-forest' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-praroha-forest text-white' : 'bg-muted'}`}>
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className="font-medium">Shipping</span>
            </div>
            <div className="h-px flex-1 bg-border" />
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-praroha-forest' : 'text-muted-foreground'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-praroha-forest text-white' : 'bg-muted'}`}>
                2
              </div>
              <span className="font-medium">Payment</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <Card className="border-border/50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <Truck className="w-6 h-6 text-praroha-forest" />
                        <h2 className="font-heading text-xl font-semibold text-praroha-forest">
                          Shipping Address
                        </h2>
                      </div>
                      
                      <form onSubmit={handleAddressSubmit} className="space-y-4" data-testid="shipping-form">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="full_name">Full Name *</Label>
                            <Input
                              id="full_name"
                              value={shippingAddress.full_name}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, full_name: e.target.value })}
                              placeholder="John Doe"
                              required
                              data-testid="shipping-name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number *</Label>
                            <Input
                              id="phone"
                              value={shippingAddress.phone}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, phone: e.target.value })}
                              placeholder="+91 98765 43210"
                              required
                              data-testid="shipping-phone"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="address_line1">Address Line 1 *</Label>
                          <Input
                            id="address_line1"
                            value={shippingAddress.address_line1}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, address_line1: e.target.value })}
                            placeholder="House/Flat No., Building Name"
                            required
                            data-testid="shipping-address1"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="address_line2">Address Line 2</Label>
                          <Input
                            id="address_line2"
                            value={shippingAddress.address_line2}
                            onChange={(e) => setShippingAddress({ ...shippingAddress, address_line2: e.target.value })}
                            placeholder="Street, Area, Landmark"
                            data-testid="shipping-address2"
                          />
                        </div>
                        
                        <div className="grid sm:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="city">City *</Label>
                            <Input
                              id="city"
                              value={shippingAddress.city}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                              placeholder="Pune"
                              required
                              data-testid="shipping-city"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="state">State *</Label>
                            <Input
                              id="state"
                              value={shippingAddress.state}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                              placeholder="Maharashtra"
                              required
                              data-testid="shipping-state"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="pincode">Pincode *</Label>
                            <Input
                              id="pincode"
                              value={shippingAddress.pincode}
                              onChange={(e) => setShippingAddress({ ...shippingAddress, pincode: e.target.value })}
                              placeholder="411001"
                              required
                              data-testid="shipping-pincode"
                            />
                          </div>
                        </div>
                        
                        <Button
                          type="submit"
                          className="w-full bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full py-6 mt-6"
                          data-testid="continue-to-payment"
                        >
                          Continue to Payment
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <Card className="border-border/50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <CreditCard className="w-6 h-6 text-praroha-forest" />
                        <h2 className="font-heading text-xl font-semibold text-praroha-forest">
                          Payment
                        </h2>
                      </div>
                      
                      {/* Shipping Address Summary */}
                      <div className="bg-praroha-cream/50 rounded-xl p-4 mb-6">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-praroha-forest">{shippingAddress.full_name}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {shippingAddress.address_line1}
                              {shippingAddress.address_line2 && `, ${shippingAddress.address_line2}`}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {shippingAddress.city}, {shippingAddress.state} - {shippingAddress.pincode}
                            </p>
                            <p className="text-sm text-muted-foreground">{shippingAddress.phone}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setStep(1)}
                            className="text-praroha-forest"
                          >
                            Edit
                          </Button>
                        </div>
                      </div>

                      <div className="text-center py-8">
                        <CreditCard className="w-16 h-16 text-praroha-forest mx-auto mb-4" />
                        <p className="text-muted-foreground mb-6">
                          You will be redirected to Razorpay secure payment gateway.
                        </p>
                        <Button
                          onClick={handlePayment}
                          disabled={loading}
                          className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-12 py-6 text-lg"
                          data-testid="pay-now-btn"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>Pay ₹{isSubscriptionCheckout ? subscriptionPlan.price : cart.total?.toFixed(0)}</>                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24 border-border/50 shadow-soft" data-testid="checkout-summary">
                <CardContent className="p-6">
                  <h2 className="font-heading text-lg font-semibold text-praroha-forest mb-4">
                    Order Summary
                  </h2>
                  
                  {isSubscriptionCheckout ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-praroha-cream/50 rounded-xl">
                        <div className="w-10 h-10 rounded-lg bg-praroha-forest/10 flex items-center justify-center">
                          <RefreshCw className="w-5 h-5 text-praroha-forest" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-praroha-forest">
                            Subscription Plan {subscriptionPlan.plan}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {subscriptionPlan.quantity} x 4 boxes/month (weekly)
                          </p>
                        </div>
                      </div>
                      <Separator className="my-2" />
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Monthly (4 deliveries)</span>
                          <span>₹{subscriptionPlan.price}</span>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Per week</span>
                          <span>₹{subscriptionPlan.weeklyPrice}</span>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Shipping</span>
                          <span className="text-praroha-sage">Free</span>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex justify-between">
                          <span className="font-heading font-semibold text-praroha-forest">Total/month</span>
                          <span className="font-heading font-semibold text-praroha-forest">
                            ₹{subscriptionPlan.price}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                  <div className="space-y-3 max-h-64 overflow-auto">
                    {cart.items?.map((item, index) => (
                      <div key={index} className="flex gap-3">
                        <img
                          src={item.product?.image}
                          alt={item.product?.name}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-praroha-forest truncate">
                            {item.product?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Qty: {item.quantity}
                          </p>
                        </div>
                        <p className="text-sm font-medium text-praroha-forest">
                          ₹{item.item_total?.toFixed(0)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <Separator className="my-4" />
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Subtotal</span>
                      <span>₹{cart.total?.toFixed(0)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Shipping</span>
                      <span className="text-praroha-sage">Free</span>
                    </div>
                    <Separator className="my-2" />
                    <div className="flex justify-between">
                      <span className="font-heading font-semibold text-praroha-forest">Total</span>
                      <span className="font-heading font-semibold text-praroha-forest">
                        ₹{cart.total?.toFixed(0)}
                      </span>
                    </div>
                  </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
